import { useState, useCallback, useEffect, useRef, RefObject } from 'react';

/**
 * Настройки масштабирования.
 */
interface ZoomConfig {
  /** Минимальный масштаб */
  minScale?: number;
  /** Максимальный масштаб */
  maxScale?: number;
  /** Шаг изменения масштаба */
  step?: number;
  /** Начальный масштаб */
  initialScale?: number;
}

/**
 * Возвращаемые значения хука.
 */
interface UseDashboardZoomReturn {
  /** Текущий масштаб */
  scale: number;
  /** Увеличить масштаб */
  zoomIn: () => void;
  /** Уменьшить масштаб */
  zoomOut: () => void;
  /** Сбросить масштаб до 100% */
  resetZoom: () => void;
  /** Установить конкретный масштаб */
  setScale: (scale: number) => void;
  /** Обработчик колёсика мыши (для Ctrl+Wheel) */
  handleWheel: (e: WheelEvent) => void;
}

/**
 * Хук для управления масштабированием дашборда.
 * Поддерживает:
 * - Увеличение/уменьшение кнопками
 * - Ctrl + колёсико мыши
 * - Сброс к 100%
 * 
 * @param containerRef - Ref на контейнер для привязки событий колёсика
 * @param config - Настройки масштабирования
 */
export const useDashboardZoom = (
  containerRef: RefObject<HTMLDivElement | null>,
  config: ZoomConfig = {}
): UseDashboardZoomReturn => {
  const {
    minScale = 0.1,
    maxScale = 3,
    step = 0.1,
    initialScale = 1,
  } = config;

  const [scale, setScaleState] = useState(initialScale);

  /**
   * Ограничивает значение масштаба в допустимых пределах.
   */
  const clampScale = useCallback(
    (value: number): number => {
      return Math.max(minScale, Math.min(value, maxScale));
    },
    [minScale, maxScale]
  );

  /**
   * Установить масштаб с проверкой границ.
   */
  const setScale = useCallback(
    (newScale: number) => {
      setScaleState(clampScale(newScale));
    },
    [clampScale]
  );

  /**
   * Увеличить масштаб на один шаг.
   */
  const zoomIn = useCallback(() => {
    setScaleState((prev) => clampScale(prev + step));
  }, [clampScale, step]);

  /**
   * Уменьшить масштаб на один шаг.
   */
  const zoomOut = useCallback(() => {
    setScaleState((prev) => clampScale(prev - step));
  }, [clampScale, step]);

  /**
   * Сбросить масштаб до 100%.
   */
  const resetZoom = useCallback(() => {
    setScaleState(1);
  }, []);

  /**
   * Обработчик колёсика мыши.
   * Масштабирует только при зажатом Ctrl.
   */
  const handleWheel = useCallback(
    (e: WheelEvent) => {
      if (e.ctrlKey) {
        e.preventDefault();
        const scaleChange = e.deltaY > 0 ? -step : step;
        setScaleState((prev) => clampScale(prev + scaleChange));
      }
    },
    [clampScale, step]
  );

  /**
   * Привязка обработчика колёсика к контейнеру.
   */
  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener('wheel', handleWheel, { passive: false });
      return () => {
        container.removeEventListener('wheel', handleWheel);
      };
    }
  }, [containerRef, handleWheel]);

  return {
    scale,
    zoomIn,
    zoomOut,
    resetZoom,
    setScale,
    handleWheel,
  };
};

export default useDashboardZoom;
